import {
  buildLocalRegistryMiniSearch,
  searchLocalRegistryMiniSearch,
} from './buildLocalRegistryMiniSearch'

// Curated static quick-actions. Each entry exposes a logical `action` token
// that the UI maps to a side-effect (creating a new set/question/repo/section).
// Adding a new quick action is a single entry here plus a matching arm in
// `onSuggestionSelected` inside the top-nav search bar.
//
// Hits are rendered as Algolia-shaped objects with __quickAction: true and
// __localOrigin: true. They surface in two places:
//   1. The dropdown empty-state (focused, no query) — full list, no fuzz.
//   2. Mid-query results — fuzzy-matched against `label` + `keywords`.

// `prefill: true` marks entries shown in the dropdown empty-state (focused, no
// query). The empty-state list is built by filtering on this flag, so adding a
// future prefill entry is a one-line change here (and in the nav/external
// registries) with no consumer changes.
export const STATIC_QUICK_ACTIONS = [
  {
    id: 'qa-new-set',
    label: 'New Set',
    icon: 'compose',
    action: 'newSet',
    keywords: ['create', 'add', 'set', 'new'],
    prefill: true,
  },
  {
    id: 'qa-new-pack',
    label: 'New Pack',
    icon: 'add-pack',
    action: 'newRepo',
    keywords: ['create', 'add', 'pack', 'repo', 'new'],
    prefill: true,
  },
  {
    id: 'qa-new-class',
    label: 'New Class',
    icon: 'add-class',
    action: 'newSection',
    keywords: ['create', 'add', 'class', 'section', 'new'],
    prefill: true,
  },
  {
    id: 'qa-upgrade-pro',
    label: 'Upgrade to Pro',
    icon: 'gear',
    action: 'upgradePro',
    keywords: ['pro', 'upgrade', 'billing', 'subscription', 'plan', 'premium', 'paid'],
    prefill: false,
  },
]

/**
 * Project a raw entry into the Algolia-shaped hit consumed by the dropdown.
 * Used for the empty-state list where no query is run.
 */
export function toQuickActionHit(entry) {
  return {
    ...entry,
    objectID: entry.id,
    __quickAction: true,
    __localOrigin: true,
  }
}

/**
 * Build a MiniSearch index over the quick-actions list. Caller is expected to
 * memoise the entries reference; this function is a thin allocator.
 */
export function buildQuickActionsMiniSearchIndex(entries = STATIC_QUICK_ACTIONS) {
  return buildLocalRegistryMiniSearch(entries)
}

/**
 * Run a query against the MiniSearch quick-actions index and return
 * Algolia-shaped flat hits, each carrying __quickAction: true,
 * __localOrigin: true, __localEngine: 'minisearch' and __minisearchScore.
 */
export function searchQuickActionsMiniSearch(ms, query, limit = 6) {
  if (!ms || !query) return []
  const byId = new Map(STATIC_QUICK_ACTIONS.map((e) => [e.id, e]))
  return searchLocalRegistryMiniSearch(ms, query, undefined, limit).map((result) => {
    const entry = byId.get(result.id) || { id: result.id, label: result.label }
    return {
      ...toQuickActionHit(entry),
      __localEngine: 'minisearch',
      __minisearchScore: result.score,
      __minisearchTerms: result.matchedTerms,
    }
  })
}
